//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: URL data about the api calls we need to make
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   02.07.21 Sean Flook          WI39??? Initial Revision.
//    002   02.06.23 Joel Benford        WI40689 Invoke homepage control
//    003   23.08.23 Sean Flook        IMANN-159 Include the street template and sub-locality lookup.
//    004   07.09.23 Sean Flook                  Remove unnecessary awaits.
//    005   02.01.24 Sean Flook                  Changed console.log to console.error for error messages.
//    006   05.01.24 Sean Flook                  Changes to sort out warnings.
//    007   25.01.24 Sean Flook                  Correctly handle status code 204.
//    008   13.02.24 Sean Flook                  Correctly handle the response from the GET endpoints.
//    009   26.02.24 Joel Benford      IMANN-242 Add DbAuthority to lookups context
//    010   09.02.24 Joel Benford     IM-227/228 Generalize ward/parish URL
//    011   19.06.24 Sean Flook        IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    012   05.07.24 Sean Flook        IMANN-629 If we cannot return the apiMetadata then it means we are looking at different database and need to expire the current user.
//    013   10.09.24 Sean Flook        IMANN-980 Only write to the console if the user has the showMessages right.
//    014   18.09.24 Sean Flook        IMANN-980 Added missing parameter when calling fetchData.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    015   30.10.24 Sean Flook       IMANN-1040 Display a message dialog if we get an error loading the lookups etc.
//endregion Version 1.0.1.0
//region Version 1.0.5.0
//    016   30.01.25 Sean Flook       IMANN-1673 Changes required for new user settings API.
//    017   30.01.25 Sean Flook       IMANN-1673 Added some error handling.
//    018   17.03.25 Sean Flook       IMANN-1711 Get the metadata languages and set the metadataLanguages object in settings context.
//    019   18.03.25 Sean Flook       IMANN-1711 Ensure the startup error dialog can be seen.
//    020   18.03.25 Sean Flook       IMANN-1711 Changed so that the metadata is correctly loaded according to the authority.
//    021   24.04.25 Sean Flook       IMANN-1761 Correctly set the metadata languages for Scottish authorities.
//    022   07.05.25 Sean Flook       IMANN-1747 Fix setting the metadata languages for Scottish authorities.
//    023   28.05.25 Sean Flook       IMANN-1800 Moved the getting of the Home Page control data to here so the controls are not displayed until we have the data.
//    024   30.05.25 Sean Flook       IMANN-1800 Prevent the loading of the home page data multiple times.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    025   11.07.25 Sean Flook       IMANN-1863 No longer required to load the postcode lookups.
//    026   17.07.25 Sean Flook       IMANN-1864 No longer required to load the street descriptor lookups.
//    027   22.07.25 Sean Flook       IMANN-1855 Use the new ADSCircularProgress component.
//    028   23.07.25 Sean Flook       IMANN-1863 Ensure HomePage endpoint is only called once.
//    029   29.07.25 Joshua McCormick LoadHomePage logic to stop infinite loops
//    030   30.07.25 Joshua McCormick IMANN-1925 propertyEditsCutOffDate changed to 14 days from 30 days
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    031   27.08.25 Sean Flook       IMANN-1812 Only load the metadata and wards and parishes when we have the dataProviderCode.
//    032   19.09.25 Sean Flook       IMANN-2014 Added some error trapping.
//    033   06.11.25 Sean Flook       IMANN-2092 Set the spatialReferenceWkid variable.
//    034   14.11.25 Sean Flook       IMANN-2092 Set the spatialReferenceWkid variable using the new wkid field in the authority details.
//    035   19.02.26 Sean Flook       IMANN-2330 Changed iManage Cloud to iManage GMS.
//endregion Version 1.0.7.0
//region Version 1.0.7.2
//    036   14.05.26 Sean Flook       IMANN-2330 Correctly set the metadata languages when a GeoPlace authority user does not have ASD access.
//endregion Version 1.0.7.2
//
//--------------------------------------------------------------------------------------------------
//endregion header */

import React, { useContext, useState, useRef, useEffect } from "react";

import LookupContext from "../context/lookupContext";
import SettingsContext from "../context/settingsContext";
import UserContext from "../context/userContext";
import MapContext from "../context/mapContext";

import {
  GetValidationMessagesUrl,
  GetLocalityUrl,
  GetTownUrl,
  GetIslandUrl,
  GetAdministrativeAreaUrl,
  GetOperationalDistrictUrl,
  GetAppCrossRefUrl,
  GetSubLocalityUrl,
  GetPostTownUrl,
  GetWardsUrl,
  GetParishesUrl,
  GetDbAuthorityUrl,
  GetApiMetadataUrl,
  GetLookupMetadataUrl,
  GetSettingsMetadataUrl,
  GetAuthorityDetailsUrl,
  GetPropertyTemplatesUrl,
  GetStreetTemplateUrl,
  GetMapLayersUrl,
  GetLSGMetadataUrl,
  GetASDMetadataUrl,
  GetLLPGMetadataUrl,
  GetHomepageUrl,
} from "../configuration/ADSConfig";

import { getMonthString } from "../utils/HelperUtils";

import ADSCircularProgress from "../components/ADSCircularProgress";
import ADSHomepageControl from "../components/ADSHomepageControl";
import MessageDialog from "../dialogs/MessageDialog";

const HomePage = () => {
  const lookupsContext = useContext(LookupContext);
  const settingsContext = useContext(SettingsContext);
  const userContext = useContext(UserContext);
  const mapContext = useContext(MapContext);

  const [lookupValidationMessages, setLookupValidationMessages] = useState();
  const [lookupLocalities, setLookupLocalities] = useState();
  const [lookupIslands, setLookupIslands] = useState();
  const [lookupTowns, setLookupTowns] = useState();
  const [lookupAdminAuthorities, setLookupAdminAuthorities] = useState();
  const [lookupOperationalDistricts, setLookupOperationalDistricts] = useState();
  const [lookupAppCrossRefs, setLookupAppCrossRefs] = useState();
  const [lookupSubLocalities, setLookupSubLocalities] = useState();
  const [lookupPostTowns, setLookupPostTowns] = useState();
  const [lookupWards, setLookupWards] = useState();
  const [lookupParishes, setLookupParishes] = useState();
  const [lookupDbAuthorities, setLookupDbAuthorities] = useState();
  const [apiMetadata, setApiMetadata] = useState();
  const [lookupMetadata, setLookupMetadata] = useState();
  const [settingsMetadata, setSettingsMetadata] = useState();
  const [authorityDetails, setAuthorityDetails] = useState();
  const [propertyTemplates, setPropertyTemplates] = useState();
  const [streetTemplate, setStreetTemplate] = useState();
  const [mapLayers, setMapLayers] = useState();
  const [streetMetadata, setStreetMetadata] = useState();
  const [asdMetadata, setAsdMetadata] = useState();
  const [propertyMetadata, setPropertyMetadata] = useState();
  const [homePageData, setHomePageData] = useState();

  const loadedLookups = useRef([]);
  const validationMessagesLoaded = useRef(false);
  const localitiesLoaded = useRef(false);
  const islandsLoaded = useRef(false);
  const townsLoaded = useRef(false);
  const adminAuthoritiesLoaded = useRef(false);
  const operationalDistrictsLoaded = useRef(false);
  const appCrossRefsLoaded = useRef(false);
  const subLocalitiesLoaded = useRef(false);
  const postTownsLoaded = useRef(false);
  const wardsLoaded = useRef(false);
  const parishesLoaded = useRef(false);
  const dbAuthoritiesLoaded = useRef(false);
  const apiMetadataLoaded = useRef(false);
  const lookupMetadataLoaded = useRef(false);
  const settingsMetadataLoaded = useRef(false);
  const authorityDetailsLoaded = useRef(false);
  const propertyTemplatesLoaded = useRef(false);
  const streetTemplateLoaded = useRef(false);
  const mapLayersLoaded = useRef(false);
  const streetMetadataLoaded = useRef(false);
  const asdMetadataLoaded = useRef(false);
  const propertyMetadataLoaded = useRef(false);
  const homePageDataLoaded = useRef(false);
  const runningHomePageLoad = useRef(false);

  const [isLoaded, setIsLoaded] = useState(false);
  const [openMessageDialog, setOpenMessageDialog] = useState(false);

  /**
   * Method to fetch the lookup data.
   *
   * @param {object} lookup The lookup object.
   * @param {object} userContext The userContext object.
   */
  const fetchData = async (lookup, userContext) => {
    const setData = (id, result) => {
      switch (id) {
        case "lookupValidationMessages":
          setLookupValidationMessages(result);
          validationMessagesLoaded.current = true;
          break;

        case "lookupLocalities":
          setLookupLocalities(result);
          localitiesLoaded.current = true;
          break;

        case "lookupIslands":
          setLookupIslands(result);
          islandsLoaded.current = true;
          break;

        case "lookupTowns":
          setLookupTowns(result);
          townsLoaded.current = true;
          break;

        case "lookupAdminAuthorities":
          setLookupAdminAuthorities(result);
          adminAuthoritiesLoaded.current = true;
          break;

        case "lookupOperationalDistricts":
          setLookupOperationalDistricts(result);
          operationalDistrictsLoaded.current = true;
          break;

        case "lookupAppCrossRefs":
          setLookupAppCrossRefs(result);
          appCrossRefsLoaded.current = true;
          break;

        case "lookupSubLocalities":
          setLookupSubLocalities(result);
          subLocalitiesLoaded.current = true;
          break;

        case "lookupPostTowns":
          setLookupPostTowns(result);
          postTownsLoaded.current = true;
          break;

        case "lookupWards":
          setLookupWards(result);
          wardsLoaded.current = true;
          break;

        case "lookupParishes":
          setLookupParishes(result);
          parishesLoaded.current = true;
          break;

        case "lookupDbAuthorities":
          setLookupDbAuthorities(result);
          dbAuthoritiesLoaded.current = true;
          break;

        case "apiMetadata":
          setApiMetadata(result);
          apiMetadataLoaded.current = true;
          break;

        case "lookupMetadata":
          setLookupMetadata(result);
          lookupMetadataLoaded.current = true;
          break;

        case "settingsMetadata":
          setSettingsMetadata(result);
          settingsMetadataLoaded.current = true;
          break;

        case "authorityDetails":
          setAuthorityDetails(result);
          authorityDetailsLoaded.current = true;
          break;

        case "propertyTemplates":
          setPropertyTemplates(result);
          propertyTemplatesLoaded.current = true;
          break;

        case "streetTemplate":
          setStreetTemplate(result);
          streetTemplateLoaded.current = true;
          break;

        case "mapLayers":
          setMapLayers(result);
          mapLayersLoaded.current = true;
          break;

        case "streetMetadata":
          setStreetMetadata(result);
          streetMetadataLoaded.current = true;
          break;

        case "asdMetadata":
          setAsdMetadata(result);
          asdMetadataLoaded.current = true;
          break;

        case "propertyMetadata":
          setPropertyMetadata(result);
          propertyMetadataLoaded.current = true;
          break;

        default:
          break;
      }
    };

    if (lookup.url && lookup.url.url) {
      await fetch(lookup.url.url, {
        headers: lookup.url.headers,
        crossDomain: true,
        method: "GET",
      })
        .then((response) => {
          if (response) {
            switch (response.status) {
              case 200:
                return response.json();

              case 204:
                return [];

              case 401:
                userContext.onExpired();
                setData(lookup.id, lookup.noRecords);
                break;

              case 500:
                if (userContext.currentUser.showMessages)
                  console.error("[500 ERROR] Fetching data", {
                    lookup: lookup.id,
                    errorText: response.statusText,
                    response: response,
                  });
                setData(lookup.id, lookup.noRecords);
                setOpenMessageDialog(true);
                break;

              default:
                if (userContext.currentUser.showMessages)
                  console.error(`[${response.status} ERROR] Fetching data`, {
                    lookup: lookup.id,
                    errorText: response.statusText,
                    response: response,
                  });
                setData(lookup.id, lookup.noRecords);
                setOpenMessageDialog(true);
                break;
            }
          } else return response.json();
        })
        .then((result) => {
          setData(lookup.id, result);
        })
        .catch((e) => {
          // Ignore lookups that do not exist
          if (e.message !== "Unexpected end of JSON input")
            if (userContext.currentUser.showMessages)
              console.error("[ERROR] Fetching data", {
                lookup: lookup.id,
                error: e,
              });
          setData(lookup.id, lookup.noRecords);
          setOpenMessageDialog(true);
        });
    } else {
      userContext.onExpired();
      setData(lookup.id, lookup.noRecords);
    }
  };

  /**
   * Event to handle when the message dialog closes.
   *
   * @param {string} action The action to take after closing the dialog.
   */
  const handleMessageDialogClose = (action) => {
    setOpenMessageDialog(false);
  };

  useEffect(() => {
    let isCancelled = false;

    /**
     * Method to load the ward and parish lookups
     */
    const LoadWardParishLookups = async () => {
      const Lookups = [
        {
          url: GetWardsUrl("GET", userContext.currentUser, Number(authorityDetails.dataProviderCode)),
          data: lookupWards,
          noRecords: [],
          id: "lookupWards",
        },
        {
          url: GetParishesUrl("GET", userContext.currentUser, Number(authorityDetails.dataProviderCode)),
          data: lookupParishes,
          noRecords: [],
          id: "lookupParishes",
        },
      ];

      Lookups.forEach(async (lookup) => {
        if (!isCancelled && !loadedLookups.current.includes(lookup.id)) {
          loadedLookups.current = loadedLookups.current.concat([lookup.id]);
          await fetchData(lookup, userContext);
        }
      });
    };

    /**
     * Method to load the metadata information.
     */
    const LoadMetadata = async () => {
      const authority = Number(authorityDetails.dataProviderCode);
      const isScottish = authority >= 9000 && authority <= 9999 && authority !== 9904;
      const Lookups = [];

      if (isScottish) {
        Lookups.push({
          url: GetLLPGMetadataUrl("GET", userContext.currentUser),
          data: propertyMetadata,
          noRecords: {},
          id: "propertyMetadata",
        });
      } else {
        if (userContext.currentUser.hasStreet)
          Lookups.push({
            url: GetLSGMetadataUrl("GET", userContext.currentUser),
            data: streetMetadata,
            noRecords: {},
            id: "streetMetadata",
          });

        if (userContext.currentUser.hasASD)
          Lookups.push({
            url: GetASDMetadataUrl("GET", userContext.currentUser),
            data: asdMetadata,
            noRecords: {},
            id: "asdMetadata",
          });

        if (userContext.currentUser.hasProperty)
          Lookups.push({
            url: GetLLPGMetadataUrl("GET", userContext.currentUser),
            data: propertyMetadata,
            noRecords: {},
            id: "propertyMetadata",
          });
      }

      Lookups.forEach(async (lookup) => {
        if (!isCancelled && !loadedLookups.current.includes(lookup.id)) {
          loadedLookups.current = loadedLookups.current.concat([lookup.id]);
          await fetchData(lookup, userContext);
        }
      });
    };

    /**
     * Method to load the lookups
     */
    const LoadLookups = async () => {
      const Lookups = [
        {
          url: GetValidationMessagesUrl(userContext.currentUser),
          data: lookupValidationMessages,
          noRecords: [],
          id: "lookupValidationMessages",
        },
        {
          url: GetLocalityUrl("GET", userContext.currentUser),
          data: lookupLocalities,
          noRecords: [],
          id: "lookupLocalities",
        },
        {
          url: GetTownUrl("GET", userContext.currentUser),
          data: lookupTowns,
          noRecords: [],
          id: "lookupTowns",
        },
        {
          url: GetIslandUrl("GET", userContext.currentUser),
          data: lookupIslands,
          noRecords: [],
          id: "lookupIslands",
        },
        {
          url: GetAdministrativeAreaUrl("GET", userContext.currentUser),
          data: lookupAdminAuthorities,
          noRecords: [],
          id: "lookupAdminAuthorities",
        },
        {
          url: GetOperationalDistrictUrl("GET", userContext.currentUser),
          data: lookupOperationalDistricts,
          noRecords: [],
          id: "lookupOperationalDistricts",
        },
        {
          url: GetAppCrossRefUrl("GET", userContext.currentUser),
          data: lookupAppCrossRefs,
          noRecords: [],
          id: "lookupAppCrossRefs",
        },
        {
          url: GetSubLocalityUrl("GET", userContext.currentUser),
          data: lookupSubLocalities,
          noRecords: [],
          id: "lookupSubLocalities",
        },
        {
          url: GetPostTownUrl("GET", userContext.currentUser),
          data: lookupPostTowns,
          noRecords: [],
          id: "lookupPostTowns",
        },
        {
          url: GetDbAuthorityUrl("GET", userContext.currentUser),
          data: lookupDbAuthorities,
          noRecords: [],
          id: "lookupDbAuthorities",
        },
        {
          url: GetApiMetadataUrl(userContext.currentUser),
          data: apiMetadata,
          noRecords: {
            iManageAPIVer: "Unknown",
            iManageCoreVer: "Unknown",
            searchIndexDllVer: "Unknown",
            searchLibDllVer: "Unknown",
            iManageDBVer: "Unknown",
            iExchangeDBVer: "Unknown",
            iValidateDBVer: "Unknown",
            iManageIndexMeta: {
              dbserver: "Unknown",
              dbname: "Unknown",
              builton: "Unknown",
              elasticnugetversion: "Unknown",
            },
          },
          id: "apiMetadata",
        },
        {
          url: GetLookupMetadataUrl(userContext.currentUser),
          data: lookupMetadata,
          noRecords: {
            iManageAPIVer: "Unknown",
            iManageCoreVer: "Unknown",
            searchIndexDllVer: "Unknown",
            searchLibDllVer: "Unknown",
            iManageDBVer: "Unknown",
            iExchangeDBVer: "Unknown",
            iValidateDBVer: "Unknown",
            iManageIndexMeta: {
              dbserver: "Unknown",
              dbname: "Unknown",
              builton: "Unknown",
              elasticnugetversion: "Unknown",
            },
          },
          id: "lookupMetadata",
        },
        {
          url: GetSettingsMetadataUrl(userContext.currentUser),
          data: settingsMetadata,
          noRecords: {
            iManageAPIVer: "Unknown",
            iManageCoreVer: "Unknown",
            searchIndexDllVer: "Unknown",
            searchLibDllVer: "Unknown",
            iManageDBVer: "Unknown",
            iExchangeDBVer: "Unknown",
            iValidateDBVer: "Unknown",
            iManageIndexMeta: {
              dbserver: "Unknown",
              dbname: "Unknown",
              builton: "Unknown",
              elasticnugetversion: "Unknown",
            },
          },
          id: "settingsMetadata",
        },
        {
          url: GetAuthorityDetailsUrl("GET", userContext.currentUser),
          data: authorityDetails,
          noRecords: {
            dataProviderCode: null,
            userOrgName: null,
            minUsrn: 0,
            maxUsrn: 0,
            minUprn: 0,
            maxUprn: 0,
            msaLicenceNumber: null,
            streetBlpu: true,
            minEsuId: 0,
            maxEsuId: 0,
            displayLanguage: "ENG",
            uppercase: false,
            databaseDescription: null,
            tabText: "iManage GMS",
          },
          id: "authorityDetails",
        },
        {
          url: GetPropertyTemplatesUrl("GET", userContext.currentUser),
          data: propertyTemplates,
          noRecords: [],
          id: "propertyTemplates",
        },
        {
          url: GetStreetTemplateUrl("GET", userContext.currentUser),
          data: streetTemplate,
          noRecords: {},
          id: "streetTemplate",
        },
        {
          url: GetMapLayersUrl("GET", userContext.currentUser),
          data: mapLayers,
          noRecords: [],
          id: "mapLayers",
        },
      ];

      Lookups.forEach(async (lookup) => {
        if (!isCancelled && !loadedLookups.current.includes(lookup.id)) {
          loadedLookups.current = loadedLookups.current.concat([lookup.id]);
          await fetchData(lookup, userContext);
        }
      });
    };

    /**
     * Method to load the home page data
     */
    const LoadHomePage = async () => {
      const propertyMaxRows = 25; //might be a setting one day
      const propertyCutOffDays = 14; //might be a setting one day
      if (homePageDataLoaded.current) return;

      var pDate = new Date();
      pDate.setDate(pDate.getDate() - propertyCutOffDays);
      const day = `0${pDate.getDate()}`.substring(0, 2);
      const month = getMonthString(pDate.getMonth()).toLowerCase();
      const year = `${pDate.getFullYear()}`;
      const queryString = `?propertyEditsCutOffDate=${day}%20${month}%20${year}&maxLatestEditResults=${propertyMaxRows}`;
      const endpoint = GetHomepageUrl(userContext.currentUser);

      await fetch(endpoint.url + queryString, {
        headers: endpoint.headers,
        crossDomain: true,
        method: "GET",
      })
        .then((res) => (res.ok ? res : Promise.reject(res)))
        .then((res) => {
          if (res.status && res.status === 204) return { pieCharts: [], latestStreetAndPropertyEdits: [] };
          else return res.json();
        })
        .then(
          (result) => {
            setHomePageData(result);
            homePageDataLoaded.current = true;
          },
          (error) => {
            if (error.status && error.status === 401) {
              userContext.onExpired();
            } else {
              if (userContext.currentUser.showMessages) console.error("[ERROR] Get Homepage data", error);
            }
            setHomePageData({ pieCharts: [], latestStreetAndPropertyEdits: [] });
            homePageDataLoaded.current = false;
          }
        );
    };

    if (!isLoaded && userContext.currentUser) {
      LoadLookups();
      if (!runningHomePageLoad.current) {
        runningHomePageLoad.current = true;
        LoadHomePage();
      }

      // We can only load the ward and parish lookups once we have the authority code
      if (authorityDetailsLoaded.current && authorityDetails && authorityDetails.dataProviderCode) {
        mapContext.onSpatialReferenceChange(authorityDetails?.wkid || 27700);
        LoadMetadata();
        LoadWardParishLookups();
      }
    } else if (!isLoaded && !userContext.currentUser) {
      userContext.onExpired();
    }

    const lookupsAreLoaded =
      validationMessagesLoaded.current &&
      localitiesLoaded.current &&
      townsLoaded.current &&
      islandsLoaded.current &&
      adminAuthoritiesLoaded.current &&
      operationalDistrictsLoaded.current &&
      appCrossRefsLoaded.current &&
      subLocalitiesLoaded.current &&
      postTownsLoaded.current &&
      wardsLoaded.current &&
      parishesLoaded.current &&
      dbAuthoritiesLoaded.current &&
      apiMetadataLoaded.current &&
      lookupMetadataLoaded.current &&
      settingsMetadataLoaded.current &&
      propertyTemplatesLoaded.current &&
      streetTemplateLoaded.current &&
      authorityDetailsLoaded.current;

    if (lookupsAreLoaded && !isLoaded) {
      lookupsContext.onLookupChange(
        lookupValidationMessages,
        lookupLocalities,
        lookupTowns,
        lookupIslands,
        lookupAdminAuthorities,
        lookupOperationalDistricts,
        lookupAppCrossRefs,
        lookupSubLocalities,
        lookupPostTowns,
        lookupWards,
        lookupParishes,
        lookupDbAuthorities
      );

      if (!apiMetadata) {
        userContext.onExpired();
      } else {
        lookupsContext.onMetadataChange(
          apiMetadata?.iManageAPIVer,
          apiMetadata?.iManageCoreVer,
          lookupMetadata?.iManageAPIVer,
          settingsMetadata?.iManageAPIVer,
          apiMetadata?.iManageDBVer,
          apiMetadata?.iExchangeDBVer,
          apiMetadata?.iValidateDBVer,
          apiMetadata?.iManageIndexMeta
        );

        settingsContext.onAuthorityDetailsChange(authorityDetails);

        settingsContext.onPropertyTemplatesChange(propertyTemplates);

        settingsContext.onStreetTemplateChange(streetTemplate);

        if (mapLayers) {
          settingsContext.onMapLayersChange(mapLayers);
        }

        const authority =
          authorityDetails && authorityDetails.dataProviderCode && !isNaN(authorityDetails.dataProviderCode)
            ? Number(authorityDetails.dataProviderCode)
            : 0;
        const isScottish = authority >= 9000 && authority <= 9999 && authority !== 9904;

        if (isScottish && propertyMetadataLoaded.current) {
          settingsContext.onMetadataLanguagesChanged(
            propertyMetadata ? propertyMetadata?.language : "ENG",
            propertyMetadata ? propertyMetadata?.language : "ENG",
            propertyMetadata ? propertyMetadata?.language : "ENG"
          );
        } else if (
          !isScottish &&
          userContext.currentUser.hasASD &&
          streetMetadataLoaded.current &&
          asdMetadataLoaded.current &&
          propertyMetadataLoaded.current
        ) {
          settingsContext.onMetadataLanguagesChanged(
            streetMetadata ? streetMetadata?.language : "ENG",
            asdMetadata ? asdMetadata?.language : "ENG",
            propertyMetadata ? propertyMetadata?.language : "ENG"
          );
        } else if (
          !isScottish &&
          !userContext.currentUser.hasASD &&
          streetMetadataLoaded.current &&
          propertyMetadataLoaded.current
        ) {
          settingsContext.onMetadataLanguagesChanged(
            streetMetadata ? streetMetadata?.language : "ENG",
            "ENG",
            propertyMetadata ? propertyMetadata?.language : "ENG"
          );
        }

        if (homePageDataLoaded.current) {
          runningHomePageLoad.current = false;
          setIsLoaded(!openMessageDialog);
        }
      }
    }

    return () => {
      isCancelled = true;
    };
  }, [
    isLoaded,
    openMessageDialog,
    lookupValidationMessages,
    lookupLocalities,
    lookupTowns,
    lookupIslands,
    lookupAdminAuthorities,
    lookupOperationalDistricts,
    lookupAppCrossRefs,
    lookupSubLocalities,
    lookupPostTowns,
    lookupWards,
    lookupParishes,
    lookupDbAuthorities,
    apiMetadata,
    lookupMetadata,
    settingsMetadata,
    authorityDetails,
    propertyTemplates,
    streetTemplate,
    mapLayers,
    streetMetadata,
    asdMetadata,
    propertyMetadata,
    lookupsContext,
    settingsContext,
    userContext,
    mapContext,
  ]);

  return isLoaded ? (
    <ADSHomepageControl data={homePageData} />
  ) : (
    <div>
      <ADSCircularProgress display={!isLoaded} message="Loading data, please wait..." />
      <MessageDialog isOpen={openMessageDialog} variant={"failLookupLoad"} onClose={handleMessageDialogClose} />
    </div>
  );
};

export default HomePage;
